import { useEffect, useMemo } from 'react'

import { Tabs } from '@elements/tabs'
import { createListLookup } from '@utils/lookup'
import { InventoryItem, Venue } from 'types/model'
import { VenueLayout, VenueLayoutSection, VenueLayoutTable } from 'types/model/venue-layout'
import { VenueMapLayer } from './venue-map-layer'
import { Button } from '@elements/button'
import { XMarkIcon } from '@heroicons/react/24/outline'

export interface VenueMapProps {
  venue: Venue
  inventory?: InventoryItem[]
  layouts?: VenueLayout[]
  sections?: VenueLayoutSection[]
  selectedSection?: VenueLayoutSection
  onSectionSelect?: (sel?: VenueLayoutSection) => void
  tables?: VenueLayoutTable[]
}

export function VenueMap({
  venue,
  layouts,
  sections,
  selectedSection,
  onSectionSelect,
  tables,
  inventory,
}: VenueMapProps) {
  const sectionsByLayout = useMemo(
    () => sections && createListLookup({ of: sections, by: 'venueLayout' }),
    [sections]
  )

  const tablesByLayout = useMemo(
    () => tables && createListLookup({ of: tables, by: 'layout' }),
    [tables]
  )

  const inventoryBySection = useMemo(
    () => inventory && createListLookup({ of: inventory, by: 'venueLayoutSection' }),
    [inventory]
  )

  const tabPanels = useMemo(
    () =>
      layouts &&
      sectionsByLayout &&
      tablesByLayout &&
      layouts
        .sort((a, b) => a.order - b.order)
        .filter((layout) => !layout.disable)
        .map((layout) => ({
          id: layout.id,
          label: (
            <span className="px-3 py-1.5 text-base text-basic transition duration-300 ease-out cursor-pointer ui-disabled:cursor-not-allowed border-b-2 border-b-transparent ui-selected:border-b-primary-400">
              {layout.name}
            </span>
          ),
          content: (
            <VenueMapLayer
              venue={venue}
              layout={layout}
              selectedSection={selectedSection}
              onSectionSelect={onSectionSelect}
              sectionInventory={inventoryBySection}
              sections={sectionsByLayout[layout.id] ?? []}
              tables={tablesByLayout[layout.id] ?? []}
            />
          ),
        })),
    [
      inventoryBySection,
      layouts,
      onSectionSelect,
      sectionsByLayout,
      selectedSection,
      tablesByLayout,
      venue,
    ]
  )

  // Add effect to preload layout images
  useEffect(() => {
    layouts?.forEach((layout) => {
      new Image().src = layout.floorPlan.small
    })
  }, [layouts])

  return (
    <div className="relative">
      {tabPanels && <Tabs panels={tabPanels} />}
      {selectedSection && (
        <Button.Floating sm rightIcon={XMarkIcon} onClick={() => onSectionSelect?.(undefined)}>
          Clear section
        </Button.Floating>
      )}
    </div>
  )
}
